package com.isaias.fyattravel.web.visa

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Headers
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.theme.ThemeColors
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.shared.visa.util.CountryCodesHelper
import org.jetbrains.compose.web.attributes.InputType

private const val FUNCTIONS_BASE = "https://us-central1-fyattravelnew.cloudfunctions.net"

// Type alias for country pair to make type inference work better
typealias CountryPair = Pair<String, String>

/**
 * Visa Requirements Page for Web
 * Matches Android's VisaScreen functionality
 */
@Composable
fun VisaPage() {
    var passportCountry by remember { mutableStateOf("UG") }
    var destinationCountry by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var result by remember { mutableStateOf<WebVisaRequirement?>(null) }
    val locale = WebLocaleStore.locale.value
    var showPassportDropdown by remember { mutableStateOf(false) }
    var showDestinationDropdown by remember { mutableStateOf(false) }
    var passportSearchQuery by remember { mutableStateOf("") }
    var destinationSearchQuery by remember { mutableStateOf("") }

    // Get all countries for selection
    val allCountries: List<CountryPair> = remember { 
        CountryCodesHelper.countries.toList().sortedBy { pair: CountryPair -> pair.second } 
    }

    // Filter countries based on search queries
    val filteredPassportCountries: List<CountryPair> = remember(passportSearchQuery) {
        if (passportSearchQuery.isBlank()) {
            allCountries
        } else {
            allCountries.filter { pair: CountryPair ->
                pair.second.contains(passportSearchQuery, ignoreCase = true) ||
                        pair.first.contains(passportSearchQuery, ignoreCase = true)
            }
        }
    }

    val filteredDestinationCountries: List<CountryPair> = remember(destinationSearchQuery) {
        if (destinationSearchQuery.isBlank()) {
            allCountries
        } else {
            allCountries.filter { pair: CountryPair ->
                pair.second.contains(destinationSearchQuery, ignoreCase = true) ||
                        pair.first.contains(destinationSearchQuery, ignoreCase = true)
            }
        }
    }

    // Get country names for display
    val passportCountryName = CountryCodesHelper.getCountryName(passportCountry) ?: passportCountry

    Div({
        style {
            maxWidth(800.px)
            property("margin", "0 auto")
        }
    }) {
        // Header
        H1({
            style {
                fontSize(2.2.em)
                fontWeight("800")
                marginBottom(0.4.em)
                color(ThemeColors.textPrimary)
            }
        }) { Text(WebStrings.t(WebStringKey.VISA_TITLE)) }

        P({
            style {
                fontSize(1.05.em)
                color(ThemeColors.textMuted)
                marginBottom(1.5.em)
            }
        }) { Text(WebStrings.t(WebStringKey.VISA_SUBTITLE)) }

        // Search Card
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                padding(1.75.em)
                borderRadius(1.em)
                property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.08)")
                marginBottom(2.em)
            }
        }) {
            // Passport Country Selector
            Div({
                style {
                    marginBottom(1.em)
                    position(Position.Relative)
                }
            }) {
                Label(null, {
                    style {
                        fontSize(0.9.em)
                        color(ThemeColors.textMuted)
                        fontWeight("600")
                        display(DisplayStyle.Block)
                        marginBottom(0.5.em)
                    }
                }) { Text(WebStrings.t(WebStringKey.VISA_PASSPORT_COUNTRY)) }

                Div({
                    style {
                        padding(0.75.em, 1.em)
                        borderRadius(0.75.em)
                        property("border", "2px solid ${ThemeColors.divider}")
                        backgroundColor(ThemeColors.surface)
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                    onClick { showPassportDropdown = !showPassportDropdown }
                }) {
                    Span({
                        style {
                            fontWeight("600")
                            color(ThemeColors.textPrimary)
                        }
                    }) {
                        Text(
                            if (passportCountry.isNotEmpty()) "$passportCountryName ($passportCountry)"
                            else WebStrings.t(WebStringKey.VISA_PASSPORT_HINT)
                        )
                    }
                    Span({ style { color(ThemeColors.textMuted) } }) { Text(if (showPassportDropdown) "▲" else "▼") }
                }

                // Passport Dropdown
                if (showPassportDropdown) {
                    Div({
                        style {
                            position(Position.Absolute)
                            top(100.percent)
                            left(0.px)
                            right(0.px)
                            backgroundColor(ThemeColors.surface)
                            borderRadius(0.75.em)
                            property("border", "2px solid ${ThemeColors.divider}")
                            property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.1)")
                            marginTop(0.5.em)
                            maxHeight(300.px)
                            overflow("auto")
                            property("z-index", "100")
                        }
                    }) {
                        // Search input inside dropdown
                        Div({
                            style {
                                padding(0.75.em)
                                property("border-bottom", "1px solid ${ThemeColors.divider}")
                                position(Position.Sticky)
                                top(0.px)
                                backgroundColor(ThemeColors.surface)
                            }
                        }) {
                            Input(type = InputType.Text, attrs = {
                                value(passportSearchQuery)
                                onInput { event -> passportSearchQuery = event.value }
                                style {
                                    width(100.percent)
                                    padding(0.5.em)
                                    borderRadius(0.5.em)
                                    property("border", "1px solid ${ThemeColors.borderLight}")
                                    fontSize(0.9.em)
                                }
                                attr("placeholder", WebStrings.get(locale, WebStringKey.VISA_PASSPORT_HINT))
                            })
                        }

                        // Country list
                        CountryList(
                            countries = filteredPassportCountries,
                            selectedCode = passportCountry,
                            onCountrySelected = { code ->
                                passportCountry = code
                                showPassportDropdown = false
                                passportSearchQuery = ""
                                result = null
                                error = null
                            }
                        )
                    }
                }
            }

            // Destination Country Selector
            Div({
                style {
                    marginBottom(1.em)
                    position(Position.Relative)
                }
            }) {
                Label(null, {
                    style {
                        fontSize(0.9.em)
                        color(ThemeColors.textMuted)
                        fontWeight("600")
                        display(DisplayStyle.Block)
                        marginBottom(0.5.em)
                    }
                }) { Text(WebStrings.t(WebStringKey.VISA_DESTINATION_COUNTRY)) }

                Div({
                    style {
                        padding(0.75.em, 1.em)
                        borderRadius(0.75.em)
                        property("border", "2px solid ${ThemeColors.divider}")
                        backgroundColor(ThemeColors.surface)
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                    onClick { showDestinationDropdown = !showDestinationDropdown }
                }) {
                    Span({
                        style {
                            fontWeight("600")
                            color(ThemeColors.textPrimary)
                        }
                    }) {
                        val destName = CountryCodesHelper.getCountryName(destinationCountry)
                        Text(
                            if (destinationCountry.isNotEmpty()) "$destName ($destinationCountry)"
                            else WebStrings.t(WebStringKey.VISA_DESTINATION_HINT)
                        )
                    }
                    Span({ style { color(ThemeColors.textMuted) } }) { Text(if (showDestinationDropdown) "▲" else "▼") }
                }

                // Destination Dropdown
                if (showDestinationDropdown) {
                    Div({
                        style {
                            position(Position.Absolute)
                            top(100.percent)
                            left(0.px)
                            right(0.px)
                            backgroundColor(ThemeColors.surface)
                            borderRadius(0.75.em)
                            property("border", "2px solid ${ThemeColors.divider}")
                            property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.1)")
                            marginTop(0.5.em)
                            maxHeight(300.px)
                            overflow("auto")
                            property("z-index", "100")
                        }
                    }) {
                        // Search input inside dropdown
                        Div({
                            style {
                                padding(0.75.em)
                                property("border-bottom", "1px solid ${ThemeColors.divider}")
                                position(Position.Sticky)
                                top(0.px)
                                backgroundColor(ThemeColors.surface)
                            }
                        }) {
                            Input(type = InputType.Text, attrs = {
                                value(destinationSearchQuery)
                                onInput { event -> destinationSearchQuery = event.value }
                                style {
                                    width(100.percent)
                                    padding(0.5.em)
                                    borderRadius(0.5.em)
                                    property("border", "1px solid ${ThemeColors.borderLight}")
                                    fontSize(0.9.em)
                                }
                                attr("placeholder", WebStrings.get(locale, WebStringKey.VISA_DESTINATION_HINT))
                            })
                        }

                        // Country list
                        CountryList(
                            countries = filteredDestinationCountries,
                            selectedCode = destinationCountry,
                            onCountrySelected = { code ->
                                destinationCountry = code
                                showDestinationDropdown = false
                                destinationSearchQuery = ""
                                result = null
                                error = null
                            }
                        )
                    }
                }
            }

            // Search Button
            Button({
                style {
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    fontSize(1.em)
                    width(100.percent)
                    marginTop(0.5.em)
                }
                onClick {
                    if (passportCountry.isBlank() || destinationCountry.isBlank()) {
                        error = "Please select both passport and destination countries"
                        return@onClick
                    }
                    if (loading) return@onClick

                    loading = true
                    error = null
                    result = null

                    val bodyJson = buildString {
                        append("{")
                        append("\"passport\":\"${passportCountry.trim().uppercase()}\",")
                        append("\"destination\":\"${destinationCountry.trim().uppercase()}\"")
                        append("}")
                    }

                    window.fetch(
                        "$FUNCTIONS_BASE/searchVisaRequirements",
                        RequestInit(
                            method = "POST",
                            headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                            body = bodyJson
                        )
                    ).then { resp ->
                        resp.text().then { text ->
                            try {
                                val obj = kotlin.js.JSON.parse<dynamic>(text)
                                val ok = (obj.success as Boolean?) == true
                                if (!ok) {
                                    error = (obj.message as String?) ?: "Failed to fetch visa requirements"
                                } else {
                                    result = WebVisaRequirement(
                                        passport = obj.requirement?.passport as String? ?: passportCountry,
                                        destination = obj.requirement?.destination as String? ?: destinationCountry,
                                        requirement = obj.requirement?.requirement as String? ?: "UNKNOWN",
                                        stay = obj.requirement?.stay as String?,
                                        notes = obj.requirement?.notes as String?,
                                        passportName = obj.requirement?.passportName as String?,
                                        destinationName = obj.requirement?.destinationName as String?,
                                        currencyCode = obj.requirement?.currencyCode as String?,
                                        embassyUrl = obj.requirement?.embassyUrl as String?,
                                        phoneCode = obj.requirement?.phoneCode as String?,
                                        timezone = obj.requirement?.timezone as String?
                                    )
                                }
                            } catch (t: dynamic) {
                                error = "Invalid response from server"
                            } finally {
                                loading = false
                            }
                        }
                    }.catch { _ ->
                        error = "Network error. Please check your connection and try again."
                        loading = false
                    }
                }
            }) {
                Text(if (loading) WebStrings.t(WebStringKey.VISA_CHECKING) else WebStrings.t(WebStringKey.VISA_CHECK_BUTTON))
            }
        }

        // Error Message
        error?.let { msg ->
            Div({
                style {
                backgroundColor(ThemeColors.errorBg)
                    color(ThemeColors.error)
                    padding(1.em)
                    borderRadius(0.75.em)
                    marginBottom(1.em)
                    fontWeight("600")
                }
            }) { Text(msg) }
        }

        // Loading State
        if (loading) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    padding(2.em)
                }
            }) {
                Div({
                    style {
                        width(40.px)
                        height(40.px)
                        borderRadius(50.percent)
                        property("border", "4px solid #333333")
                        property("border-top-color", "#AE7930")
                        property("animation", "spin 1s linear infinite")
                    }
                }) {}
            }
        }

        // Result Display
        result?.let { visa ->
            VisaResultCard(visa)
        }
    }
}

/**
 * Visa Result Card - Displays the visa requirement result
 */
@Composable
private fun VisaResultCard(visa: WebVisaRequirement) {
    val requirementType = visa.requirement.uppercase()
    val (statusColor, statusBg, statusIcon, statusText) = when (requirementType) {
        "VISA_FREE", "FREE" ->
            Quad(ThemeColors.success.toString(), ThemeColors.successBg.toString(), "✓", WebStrings.t(WebStringKey.VISA_STATUS_VISA_FREE))
        "VISA_ON_ARRIVAL", "ON_ARRIVAL" ->
            Quad(ThemeColors.primaryDark.toString(), ThemeColors.primaryMuted.toString(), "✈", WebStrings.t(WebStringKey.VISA_STATUS_VISA_ON_ARRIVAL))
        "E_VISA", "EVISA" ->
            Quad(ThemeColors.info.toString(), ThemeColors.primaryMuted.toString(), "🌐", WebStrings.t(WebStringKey.VISA_STATUS_EVISA))
        "VISA_REQUIRED", "REQUIRED" ->
            Quad(ThemeColors.error.toString(), ThemeColors.errorBg.toString(), "⚠", WebStrings.t(WebStringKey.VISA_STATUS_VISA_REQUIRED))
        else ->
            Quad(ThemeColors.textMuted.toString(), ThemeColors.skeleton.toString(), "?", WebStrings.t(WebStringKey.VISA_STATUS_UNKNOWN))
    }

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px rgba(0, 0, 0, 0.08)")
        }
    }) {
        // Status Header
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(0.75.em)
                marginBottom(1.25.em)
                padding(1.em)
                backgroundColor(Color(statusBg))
                borderRadius(0.75.em)
            }
        }) {
            Span({
                style {
                    fontSize(1.5.em)
                }
            }) { Text(statusIcon) }
            Div {
                Div({
                    style {
                        fontSize(0.85.em)
                        color(ThemeColors.textMuted)
                        fontWeight("600")
                    }
                }) { Text(WebStrings.t(WebStringKey.VISA_TITLE)) }
                Div({
                    style {
                        fontSize(1.25.em)
                        fontWeight("800")
                        color(Color(statusColor))
                    }
                }) { Text(statusText) }
            }
        }

        // Country Info
        Div({
            classes(ResponsiveStyles.twoColStackMobile)
            style {
                display(DisplayStyle.Grid)
                gap(1.em)
                marginBottom(1.25.em)
                padding(1.em)
                backgroundColor(ThemeColors.surface)
                borderRadius(0.75.em)
            }
        }) {
            Div {
                Div({
                    style {
                        fontSize(0.8.em)
                        color(ThemeColors.textMuted)
                        marginBottom(0.25.em)
                    }
                }) { Text(WebStrings.t(WebStringKey.VISA_PASSPORT_COUNTRY)) }
                Div({
                    style {
                        fontWeight("700")
                        color(ThemeColors.textPrimary)
                    }
                }) { Text(visa.passportName ?: CountryCodesHelper.getCountryName(visa.passport) ?: visa.passport) }
            }
            Div {
                Div({
                    style {
                        fontSize(0.8.em)
                        color(ThemeColors.textMuted)
                        marginBottom(0.25.em)
                    }
                }) { Text(WebStrings.t(WebStringKey.VISA_DESTINATION_COUNTRY)) }
                Div({
                    style {
                        fontWeight("700")
                        color(ThemeColors.textPrimary)
                    }
                }) { Text(visa.destinationName ?: CountryCodesHelper.getCountryName(visa.destination) ?: visa.destination) }
            }
        }

        // Stay Duration
        visa.stay?.let { stay ->
            Div({
                style {
                    marginBottom(1.em)
                }
            }) {
                Div({
                    style {
                        fontSize(0.8.em)
                        color(ThemeColors.textMuted)
                        marginBottom(0.25.em)
                    }
                }) { Text(WebStrings.format(WebStringKey.VISA_STAY_DURATION, stay)) }
            }
        }

        // Notes
        visa.notes?.let { notes ->
            if (notes.isNotBlank()) {
                Div({
                    style {
                        backgroundColor(ThemeColors.primaryMuted)
                        padding(1.em)
                        borderRadius(0.75.em)
                        marginTop(1.em)
                        property("border-left", "4px solid ${ThemeColors.primary}")
                    }
                }) {
                    Div({
                        style {
                            fontSize(0.8.em)
                            color(ThemeColors.textMuted)
                            marginBottom(0.5.em)
                            fontWeight("600")
                        }
                    }) { Text(WebStrings.t(WebStringKey.VISA_KEY_INFO_TITLE)) }
                    Div({
                        style {
                            color(ThemeColors.textMuted)
                            lineHeight("1.5")
                        }
                    }) { Text(notes) }
                }
            }
        }

        // Additional Info
        Div({
            style {
                marginTop(1.25.em)
                paddingTop(1.25.em)
                property("border-top", "1px solid #333333")
            }
        }) {
            Div({
                style {
                    fontSize(0.8.em)
                    color(ThemeColors.textSecondary)
                    marginBottom(0.75.em)
                    fontWeight("600")
                }
            }) { Text(WebStrings.t(WebStringKey.DESTINATION_INFORMATION)) }

            Div({
                classes(ResponsiveStyles.twoColStackMobile)
                style {
                    display(DisplayStyle.Grid)
                    gap(0.75.em)
                }
            }) {
                visa.currencyCode?.let {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(0.5.em)
                        }
                    }) {
                        Span({ style { color(ThemeColors.textMuted) } }) { Text("💱") }
                        Span({ style { color(ThemeColors.textMuted) } }) {
                            Text("${WebStrings.t(WebStringKey.VISA_CURRENCY)}: $it")
                        }
                    }
                }
                visa.phoneCode?.let {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(0.5.em)
                        }
                    }) {
                        Span({ style { color(ThemeColors.textMuted) } }) { Text("📞") }
                        Span({ style { color(ThemeColors.textMuted) } }) {
                            Text("${WebStrings.t(WebStringKey.VISA_PHONE_CODE)}: +$it")
                        }
                    }
                }
                visa.timezone?.let {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(0.5.em)
                        }
                    }) {
                        Span({ style { color(ThemeColors.textMuted) } }) { Text("🕐") }
                        Span({ style { color(ThemeColors.textMuted) } }) {
                            Text("${WebStrings.t(WebStringKey.VISA_TIMEZONE)}: $it")
                        }
                    }
                }
            }
        }

        // Embassy Link
        visa.embassyUrl?.let { url ->
            if (url.isNotBlank()) {
                Div({
                    style {
                        marginTop(1.25.em)
                    }
                }) {
                    A(href = url, attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(0.5.em)
                            color(ThemeColors.primary)
                            fontWeight("600")
                            textDecoration("none")
                        }
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                    }) {
                        Text(WebStrings.t(WebStringKey.VISA_EMBASSY_WEBSITE))
                    }
                }
            }
        }

        // Disclaimer
        Div({
            style {
                marginTop(1.5.em)
                padding(0.75.em)
                backgroundColor(ThemeColors.primaryMuted)
                borderRadius(0.5.em)
                fontSize(0.8.em)
                color(ThemeColors.warning)
            }
        }) {
            Text(WebStrings.t(WebStringKey.VISA_ALWAYS_VERIFY))
        }
    }
}

/**
 * Helper class for multiple return values
 */
private data class Quad<A, B, C, D>(val first: A, val second: B, val third: C, val fourth: D)

/**
 * Data class representing visa requirement result
 */
private data class WebVisaRequirement(
    val passport: String,
    val destination: String,
    val requirement: String,
    val stay: String? = null,
    val notes: String? = null,
    val passportName: String? = null,
    val destinationName: String? = null,
    val currencyCode: String? = null,
    val embassyUrl: String? = null,
    val phoneCode: String? = null,
    val timezone: String? = null
)

/**
 * Country List Composable - Renders a list of countries for selection
 */
@Composable
private fun CountryList(
    countries: List<CountryPair>,
    selectedCode: String,
    onCountrySelected: (String) -> Unit
) {
    for (i in countries.indices) {
        val pair = countries[i]
        val code = pair.first
        val name = pair.second
        Div({
            style {
                padding(0.75.em, 1.em)
                cursor("pointer")
                backgroundColor(if (code == selectedCode) ThemeColors.primaryMuted else Color.transparent)
                color(if (code == selectedCode) ThemeColors.info else ThemeColors.textMuted)
            }
            onClick {
                onCountrySelected(code)
            }
        }) {
            Text("$name ($code)")
        }
    }
}
